import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { baseUrl } from '../configs/baseUrl';
import { firebaseConfig, webPushVapidKey } from '../configs/firebase';
import { getStoredLoginResponse } from './auth-storage';

const TOKEN_KEY = 'webPushToken';
const APP = 'push';

/**
 * Registers this browser for web push (Firebase Cloud Messaging) under the signed-in user, so
 * notifications arrive with every portal tab closed. Needs notification permission and the VAPID key;
 * returns false, leaving the open-tab notifications only, when either is missing or the browser can't.
 * `page` is where a service request push opens.
 */
export async function registerWebPush(page: string): Promise<boolean> {
  if (!webPushVapidKey || Notification.permission !== 'granted' || !(await isSupported())) return false;

  const worker = await navigator.serviceWorker.register(`/assets/push-sw.js?page=${encodeURIComponent(page)}`, {
    scope: '/assets/',
  });
  const app = getApps().some((a) => a.name === APP) ? getApp(APP) : initializeApp(firebaseConfig, APP);
  const token = await getToken(getMessaging(app), { vapidKey: webPushVapidKey, serviceWorkerRegistration: worker });

  const res = await fetch(`${baseUrl()}/devices`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getStoredLoginResponse()?.accessToken}` },
    body: JSON.stringify({ token, platform: 'web', app: location.hostname, model: navigator.userAgent.slice(0, 100) }),
  });
  if (res.ok) localStorage.setItem(TOKEN_KEY, token);
  return res.ok;
}

/** Stops pushing to this browser; call on sign-out, before the stored login is cleared. */
export function forgetWebPush(): void {
  const token = localStorage.getItem(TOKEN_KEY);
  const login = getStoredLoginResponse();
  if (!token || !login) return;
  // keepalive: sign-out reloads the page straight after
  fetch(`${baseUrl()}/devices?deviceToken=${encodeURIComponent(token)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${login.accessToken}` },
    keepalive: true,
  }).catch(() => {});
}
